import type { Campaign } from "../api";

/**
 * What a campaign tested, in the reader's words.
 *
 * `build_ref` is the bench IMAGE (`pdc-maint-4d23a5a5-r26358a8-h2cd13dac`): the
 * product build plus the rig scripts and the harness it was layered with. On
 * 16 Sep 2026 Now listed three "builds" that were one DisplayOS 7.2.72 under
 * three harnesses. The name is the version the runs recorded, with the product
 * part of the tag beside it - never the harness - and the image stays one fold
 * away for whoever needs to rebuild it.
 */
export function campaignBuildName(c: Pick<Campaign, "build_ref" | "build_label" | "build_product">): string {
  const label = c.build_label ?? "";
  const product = c.build_product || c.build_ref;
  if (label && product && product !== label) return `${label} (${product})`;
  return label || product || "";
}

/** Campaigns of one product build belong together, whatever image carried it.
 *  A campaign that named no build belongs to its bench. */
export function campaignBuildKey(c: Pick<Campaign, "build_ref" | "build_product" | "bench_id">): string {
  return c.build_ref ? `build:${c.build_product || c.build_ref}` : `bench:${c.bench_id}`;
}

/** Names that point at a build rather than name one; the same list the
 *  service ignores when deciding what is new (db.POINTER_TAGS). */
export const POINTER_TAGS = new Set(["latest", "prev", "previous", "local", "current", "next", "stable"]);

/**
 * A bench image tag in words, for a list somebody picks from.
 *
 * `pdc-maint-4d23a5a5-r26358a8-h2cd13dac` read as nine near-identical strings
 * in the build list (DisplayOS bench, 28 Sep 2026). Said as the product build
 * first and what it was layered with after, the one part that differs between
 * two entries is the part that is read. A tag of another shape is itself.
 */
export function tagWords(tag: string, words: { rig: string; tests: string; alias: string }): string {
  if (POINTER_TAGS.has(tag)) return `${tag} (${words.alias})`;
  const m = /^(.+?)((?:-[rh][0-9a-f]{7,40})+)$/.exec(tag);
  if (!m) return tag;
  const parts = [m[1]];
  for (const seg of m[2].split("-").filter(Boolean)) {
    parts.push(`${seg[0] === "r" ? words.rig : words.tests} ${seg.slice(1)}`);
  }
  return parts.join(" · ");
}

/** Bench values in the order the bench gave them, pointer names last: an alias
 *  is a second name for something already in the list. */
export function pointersLast(values: string[]): string[] {
  return [...values.filter((v) => !POINTER_TAGS.has(v)), ...values.filter((v) => POINTER_TAGS.has(v))];
}
